import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LoggerService } from '@lbmx/phoenix-lib-core';
import { SpinnerService, ToastrNotificationService } from '@lbmx/phoenix-lib-utils';
import { EnabledMode, IButton, IPaging } from '@lbmx/search-container';
import { TranslocoService } from '@ngneat/transloco';
import { from, Observable, of, Subscription } from 'rxjs';
import { finalize, map, startWith, take } from 'rxjs/operators';
import { GroupToken, IAddGroupingMemberRequest, IConfigCountry, IConfigProvince, IDropDown, INewMemberLocationForm } from 'src/app/dtos/group/group.dto';
import { GroupService } from 'src/app/services/group/group.service';
import { NewMemberLocationComponent } from './new-member-location/new-member-location.component';

@Component({
  selector: 'app-new-member',
  templateUrl: './new-member.component.html',
  styleUrls: ['./new-member.component.scss']
})
export class NewMemberComponent implements OnInit, OnDestroy {

  @ViewChild(NewMemberLocationComponent) public child: NewMemberLocationComponent;

  public validToken = false;
  public subscriptions: Subscription[] = [];
  public countriesDropDown: IDropDown[];
  public provincesDropDown: IDropDown[];
  public provinces: IConfigProvince[];
  public deliveryLocations: INewMemberLocationForm[] = [{
    id: 0,
    legalcompanyName: 'company1',
    operatingName: 'company1',
    address: 'regre',
    city: 'strfewfweing',
    country: 'OD',
    province: 'GUE',
    postalCode: 'hgfhgf',
    mainPhone: '3243242',
    discountsQuestion: 'Yes',
  }, {
    id: 1,
    legalcompanyName: 'company2',
    operatingName: 'company2',
    address: 'tre',
    city: 'hgrthrt',
    country: 'OD',
    province: 'GUE',
    postalCode: 'fdgdf',
    mainPhone: '345435',
    discountsQuestion: 'Yes',
  }, {
    id: 2,
    legalcompanyName: 'company3',
    operatingName: 'company3',
    address: 'strretreing',
    city: 'trhtr',
    country: 'OD',
    province: 'GUE',
    postalCode: 'gfdgd',
    mainPhone: '123123',
    discountsQuestion: 'Yes',
  }, {
    id: 3,
    legalcompanyName: 'company4',
    operatingName: 'company4',
    address: 'tertretre',
    city: 'werwe',
    country: 'OD',
    province: 'GUE',
    postalCode: 'sadsa',
    mainPhone: '1221312',
    discountsQuestion: 'No',
  }];
  public deliveryLocations$: Observable<INewMemberLocationForm[]> = of(this.deliveryLocations);
  public showLocationDialog = false;
  public maxId = 3;
  public answersDropDown: IDropDown[] = [{
    label: 'Yes',
    value: 'Yes',
  }, {
    label: 'No',
    value: 'No',
  }];

  public buttons$: Observable<IButton[]> = this.translocoService
    .selectTranslate('ACTIONS')
    .pipe(
      startWith([]),
      map((translations) => [
        {
          name: 'new',
          label: translations.NEW,
          mode: EnabledMode.ALWAYS,
          disabled: false,
        },
        {
          name: 'edit',
          label: translations.EDIT,
          mode: EnabledMode.SINGLE,
          disabled: false,
        },
        {
          name: 'delete',
          label: translations.DELETE,
          mode: EnabledMode.MULTIPLE,
          disabled: false,
        },
      ])
    );

  public displayedColumns: string[] = [];
  public columns: any[];
  public paging: IPaging;

  public newMemberProfileForm: FormGroup = this.formBuilder.group(
    {
      legalcompanyName: ['', [Validators.required]],
      operatingName: ['', [Validators.required]],
      address: ['', [Validators.required]],
      city: ['', [Validators.required]],
      country: ['', [Validators.required]],
      province: ['', [Validators.required]],
      postalCode: ['', [Validators.required]],
      mainPhone: ['', [Validators.required]],
      discountsQuestion: ['', [Validators.required]],
      accountingName: ['', [Validators.required]],
      accountingPhone: ['', [Validators.required]],
      accountingEmail: ['', [Validators.required, Validators.email]],
    },
  );

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private loggerService: LoggerService,
    private toastSrv: ToastrNotificationService,
    private spinnerService: SpinnerService,
    private groupService: GroupService,
    private translocoService: TranslocoService,
  ) { }

  public ngOnInit(): void {
    this.spinnerService.on();
    this.validateToken();

    this.columns = [
      { field: 'legalcompanyName', header: 'Legal Company Name' },
      { field: 'operatingName', header: 'Operating Name' },
      { field: 'address', header: 'Address' },
      { field: 'city', header: 'City' },
      { field: 'country', header: 'Country' },
      { field: 'province', header: 'Province | State' },
      { field: 'postalCode', header: 'Zip | PostalCode' },
      { field: 'mainPhone', header: 'Phone Number' },
      { field: 'discountsQuestion', header: 'Does this invoiced location take advantage of terms discounts?' }
    ];
    this.displayedColumns = ['legalcompanyName', 'operatingName', 'address', 'city', 'country', 'province', 'postalCode', 'mainPhone', 'discountsQuestion'];
    this.paging = {
      sizes: [25, 50, 75, 100],
      page: { first: 0, rows: 25 },
      records: 250,
    };

    this.getCountries();
    this.getProvinces();
  }

  public getCountries() {
    this.groupService.getCountries()
      .pipe()
      .subscribe((val) => {
        this.countriesDropDown = val.map(
          (country: IConfigCountry): IDropDown => {
            return {
              label: country.countryName,
              value: country.countryCode.trim(),
            };
          }
        );
      });
  }

  public getProvinces() {
    this.groupService.getProvinces()
      .pipe()
      .subscribe((val) => {
        this.provinces = val;
      });
  }

  public onCountryDropDownSelect() {
    const filteredProv = this.provinces.filter(prov => prov.countryCode === this.newMemberProfileForm.get('country').value);
    this.provincesDropDown = filteredProv.map((province: IConfigProvince): IDropDown => {
      return {
        label: province.provinceName,
        value: province.provinceCode.trim(),
      };
    });
  }

  public validateToken(): void {
    this.subscriptions.push(
      this.groupService
        .validateGroupLink(this.buildValidateTokenRequest())
        .pipe(take(1))
        .subscribe(
          (res) => {
            if (res?.status === 'Ok') {
              this.loggerService.info(
                'groupTokenResponse',
                'GroupService.validateGroupLink',
                JSON.stringify(res)
              );
              this.validToken = true;
            }
          },
          (err) => {
            this.loggerService.error(
              'Error group token',
              'GroupService.validateGroupLink',
              err
            );
            this.validToken = false;
          }
        )
        .add(() => {
          this.spinnerService.off();
        })
    );
  }

  public ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
    this.spinnerService.off();
  }

  public buildAddRequest(): IAddGroupingMemberRequest {
    return {
      token: this.route.snapshot.queryParamMap.get('token'),
      legalcompanyName: this.newMemberProfileForm.value.legalcompanyName,
      operatingName: this.newMemberProfileForm.value.operatingName,
      address: this.newMemberProfileForm.value.address,
      city: this.newMemberProfileForm.value.city,
      country: this.newMemberProfileForm.value.country,
      province: this.newMemberProfileForm.value.province,
      postalCode: this.newMemberProfileForm.value.postalCode,
      mainPhone: this.newMemberProfileForm.value.mainPhone,
      discountsQuestion: this.newMemberProfileForm.value.discountsQuestion,
      accountingName: this.newMemberProfileForm.value.accountingName,
      accountingPhone: this.newMemberProfileForm.value.accountingPhone,
      accountingEmail: this.newMemberProfileForm.value.accountingEmail,
      groupMemberLocations: JSON.stringify(this.deliveryLocations),
    };
  }

  private buildValidateTokenRequest(): GroupToken {
    const request: GroupToken = new GroupToken();
    request.token = this.route.snapshot.queryParamMap.get('token');
    return request;
  }

  public save() {
    this.addNewMember();
  }

  public addNewMember() {
    this.spinnerService.on();
    this.subscriptions.push(
      this.groupService
        .submitNewMember(this.buildAddRequest())
        .pipe(finalize(() => this.spinnerService.off()))
        .subscribe(
          (response) => {
            this.loggerService.info(
              'AddMemberResponse',
              'AddMemberService.addMember',
              JSON.stringify(response)
            );
            if (response.wasAdded === true) {
              this.toastSrv.successNotify(
                this.translocoService.translate(
                  'ONBOARDING.ADD_MEMBER_SUCCESS'
                ),
                null,
                5000
              );
            }
          },
          (error) => {
            if (error.error != null && error.error.errorCode === 3) {
              this.toastSrv.warnNotify(
                this.translocoService.translate(
                  'ONBOARDING.MEMBER_ALREADY_EXIST'
                ),
                null,
                5000
              );
            } else {
              this.toastSrv.errorNotify(
                this.translocoService.translate(
                  'ONBOARDING.ADD_MEMBER_FAILURE'
                ),
                null,
                5000
              );
            }
            this.loggerService.error(
              'AddMemberResponse',
              'AddMemberService.addMember',
              JSON.stringify(error)
            );
          }
        )
        .add(() => {
          this.newMemberProfileForm.reset();
          this.deliveryLocations = [];
          this.deliveryLocations$ = of(this.deliveryLocations);
        })
    );
  }

  public onToolbarClick({ command, selection, clearSelection }) {
    switch (command) {
      case 'new':
        this.showLocationDialog = true;
        clearSelection();
        break;
      case 'edit':
        this.editLocation(selection, clearSelection);
        break;
      case 'delete':
        this.deleteLocations(selection, clearSelection);
        break;
      default:
        break;
    }
  }

  public editLocation(selectedLocation: INewMemberLocationForm[], clearSelection) {
    this.child.prefillForm(selectedLocation[0]);
    clearSelection();
    this.showLocationDialog = true;
  }

  public deleteLocations(selectedLocations: INewMemberLocationForm[], clearSelection) {
    from(selectedLocations).subscribe(
      (selectedLocation) => {
        this.deliveryLocations$.subscribe(current => {
          const pos = current.findIndex(location => selectedLocation.id === location.id);
          current.splice(pos, 1);
        });
      }
    );
    clearSelection();
  }

  public closeLocationDialog(deliveryLocation: INewMemberLocationForm) {
    this.showLocationDialog = false;
    if (deliveryLocation != null) {
      of(deliveryLocation).subscribe(
        (location) => {
          this.deliveryLocations$.subscribe(current => {
            if (location.id === -1) {
              location.id = ++this.maxId;
              current.push(location);
            } else {
              const pos = current.findIndex(loc => location.id === loc.id);
              if (pos > -1) {
                current[pos] = location;
              }
            }
          });
        }
      );
    }
  }
}
